import { PublicRatingScope } from 'components/ReviewAudit/PublicRatings'
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import { Card, CardBody, Container, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import SearchBox from "components/Common/SearchBox"
import { capitalize } from "lodash"
import listColumns, { ORGANIZER_TIMEZONE } from "./listColumns"
import FilterList from "./FilterList"
import { getOrganizers, getOrganizersLocations } from "./actions"
import { readOrganizerQuery, writeOrganizerQuery, organizerDateError } from "./listQuery"
import "../../../assets/scss/custom/pages/_clubAccounts.scss"
import "../../../assets/scss/custom/pages/_organizers.scss"

const FILTER_LABELS = {
  search: "Search", location: "Location", accountType: "Account type", status: "Status",
  fromDate: "Account created from", toDate: "Account created to",
}
const filterLabel = (key, value) => `${FILTER_LABELS[key]}: ${key === "accountType" && value === "unspecified"
  ? "Not specified" : key === "status" || key === "accountType" ? capitalize(value) : value.replace(/^null, /, "")}`

export const OrganizerDirectory = ({
  organizers = {}, getOrganizers, getOrganizersLocations, organizersLocations = [],
  listLoading = false, listError = "", locationsError = "", locationsLoading = false,
  history, location,
}) => {
  const { filters, options } = useMemo(() => readOrganizerQuery(location.search), [location.search])
  const columns = useMemo(() => listColumns(location.search), [location.search])
  const [searchText, setSearchText] = useState(filters.search || "")
  const [advancedFilters, setAdvancedFilters] = useState(Boolean(filters.fromDate || filters.toDate))
  const searchTimer = useRef()
  const dateError = organizerDateError(filters)
  const facetFilters = useMemo(() => ({
    ...(filters.accountType && { accountType: filters.accountType }),
    ...(filters.status && { status: filters.status }),
    ...(filters.fromDate && { fromDate: filters.fromDate }),
    ...(filters.toDate && { toDate: filters.toDate }),
  }), [filters.accountType, filters.status, filters.fromDate, filters.toDate])

  useEffect(() => {
    if (!dateError) getOrganizers(filters, options)
  }, [filters, options, dateError, getOrganizers])
  useEffect(() => {
    if (!dateError) getOrganizersLocations(facetFilters)
  }, [facetFilters, dateError, getOrganizersLocations])
  useEffect(() => {
    clearTimeout(searchTimer.current)
    setSearchText(filters.search || "")
    if (filters.fromDate || filters.toDate) setAdvancedFilters(true)
  }, [filters])
  useEffect(() => () => clearTimeout(searchTimer.current), [])

  const updateQuery = useCallback((nextFilters, nextOptions) => {
    const search = writeOrganizerQuery(nextFilters, nextOptions)
    if (search !== history.location.search) history.replace({ ...history.location, search })
  }, [history])
  const changeFilters = patch => {
    clearTimeout(searchTimer.current)
    updateQuery({ ...filters, search: searchText, ...patch }, { ...options, page: 1 })
  }
  const changeOptions = patch => {
    clearTimeout(searchTimer.current)
    const pendingSearch = searchText.trim() !== (filters.search || "")
    updateQuery({ ...filters, search: searchText }, {
      ...options, ...patch, ...(pendingSearch && { page: 1 }),
    })
  }
  const changeSearch = value => {
    setSearchText(value)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      const current = readOrganizerQuery(history.location.search)
      updateQuery({ ...current.filters, search: value }, { ...current.options, page: 1 })
    }, 400)
  }
  // the „×”: the whole list at once, without the typing delay
  const clearSearch = () => {
    clearTimeout(searchTimer.current)
    setSearchText("")
    const current = readOrganizerQuery(history.location.search)
    updateQuery({ ...current.filters, search: "" }, { ...current.options, page: 1 })
  }
  const clearFilters = () => {
    clearTimeout(searchTimer.current)
    setSearchText("")
    updateQuery({}, { ...options, page: 1 })
  }

  const total = Number(organizers.totalResults) || 0
  const totalPages = Math.max(1, Math.ceil(total / options.limit))
  const rows = organizers.results || []
  const ready = !dateError && !listLoading && !listError && organizers.totalResults != null
    && organizers.queryKey === writeOrganizerQuery(filters, options)
  useEffect(() => {
    if (ready && organizers.page !== options.page) updateQuery(filters, { ...options, page: organizers.page })
  }, [ready, organizers.page, filters, options, updateQuery])
  const first = rows.length ? (options.page - 1) * options.limit + 1 : 0
  const last = rows.length ? first + rows.length - 1 : 0
  const activeFilters = Object.entries(filters)
  const dateFilterCount = [filters.fromDate, filters.toDate].filter(Boolean).length

  return (
    <PublicRatingScope kind="organizers" ids={rows.map(row => row.id || row._id)}><div className="page-content">
      <Container fluid>
        <Breadcrumbs title="User Accounts" breadcrumbItem="Organizers" />
        <Card className="clubs-directory-card organizers-directory">
          <CardBody className="p-0">
            <div className="club-directory-toolbar">
              <div className="club-directory-heading">
                <div>
                  <h4 className="mb-1">Organizers {ready && <span className="text-muted">({total.toLocaleString()})</span>}</h4>
                  <p className="text-muted mb-0">Review organizers, their contacts and assigned clubs.</p>
                </div>
              </div>
              <div className="club-search-row">
                <SearchBox look="pill" className="club-search-box" aria-label="Search organizers" value={searchText}
                  maxLength={120} onValueChange={changeSearch} onClear={clearSearch}
                  placeholder="Search name, #ID, email or phone" />
                <button type="button" className="btn btn-light club-more-filters"
                  aria-expanded={advancedFilters} aria-controls="organizer-date-filters"
                  onClick={() => setAdvancedFilters(value => !value)}>
                  <i className="bx bx-slider-alt mr-1" aria-hidden="true" />
                  More filters {dateFilterCount > 0 && <span className="badge badge-primary ml-1">{dateFilterCount}</span>}
                </button>
              </div>
              <FilterList filters={filters} onChange={changeFilters} locations={organizersLocations}
                advancedFilters={advancedFilters} sortBy={options.sortBy}
                onSort={sortBy => changeOptions({ sortBy, page: 1 })}
                locationsLoading={locationsLoading} dateError={dateError} />
              <p id="organizer-timezone" className="text-muted font-size-12 mt-2 mb-0">
                Account creation dates and times: {ORGANIZER_TIMEZONE}. Date filters include both selected days.
              </p>
              {locationsError && <div className="text-muted mt-2" role="status">
                {locationsError} <button className="btn btn-link btn-sm" type="button"
                  onClick={() => getOrganizersLocations(facetFilters)}>Retry locations</button>
              </div>}
              {(activeFilters.length > 0 || searchText) && <div className="club-active-filters">
                {activeFilters.map(([key, value]) => (
                  <button key={key} type="button" className="club-filter-chip"
                    aria-label={`Remove ${FILTER_LABELS[key]} filter`}
                    onClick={() => changeFilters({ [key]: "" })}>
                    {filterLabel(key, value)} <i className="bx bx-x" aria-hidden="true" />
                  </button>
                ))}
                <button className="btn btn-link btn-sm club-clear-filters" type="button" onClick={clearFilters}>Clear all</button>
              </div>}
            </div>

            {dateError ? <div className="organizer-list-state text-danger" id="organizer-date-error" role="alert">{dateError}</div>
              : listError ? <div className="organizer-list-state" role="alert">
                <p>{listError}</p>
                <button className="btn btn-primary" type="button" onClick={() => getOrganizers(filters, options)}>Retry organizers</button>
              </div>
              : !ready ? <div className="organizer-list-state" role="status"><Spinner size="sm" className="mr-2" />Loading organizers…</div>
              : rows.length === 0 ? <div className="organizer-list-state" role="status">
                <p>{options.page > totalPages ? "This page is no longer available." : activeFilters.length
                  ? "No organizers match the current filters." : "No organizers are available."}</p>
                {options.page > 1 ? <button className="btn btn-light" type="button"
                  onClick={() => changeOptions({ page: 1 })}>Go to first page</button> :
                  activeFilters.length > 0 && <span className="text-muted">Adjust the filters or clear them to see all organizers.</span>}
              </div> : <div className="table-responsive club-directory-table">
                <table className="table table-hover" aria-label="Organizers">
                  <thead className="thead-light"><tr>{columns.map(column => <th scope="col" key={column.dataField}>{column.text}</th>)}</tr></thead>
                  <tbody>{rows.map(organizer => <tr key={organizer.id}>
                    {columns.map(column => <td key={column.dataField} data-label={column.text}>
                      {column.formatter(organizer[column.dataField], organizer)}
                    </td>)}
                  </tr>)}</tbody>
                </table>
              </div>}

            {ready && rows.some(account => account.clubsUnavailable || account.ownerLookupUnavailable) && <div className="organizer-list-state" role="status">
              Some club details could not be loaded. <button type="button" className="btn btn-link" onClick={() => getOrganizers(filters, options)}>Retry clubs</button>
            </div>}
            {ready && <div className="club-directory-footer">
              <div className="club-result-summary" role="status">{first}–{last} of {total.toLocaleString()} organizers</div>
              <nav aria-label="Organizer pages" className="organizer-pagination">
                <button type="button" className="btn btn-light btn-sm" aria-label="Previous page"
                  disabled={options.page <= 1} onClick={() => changeOptions({ page: options.page - 1 })}>
                  <i className="bx bx-chevron-left" aria-hidden="true" />
                </button>
                <span>Page {options.page} of {totalPages}</span>
                <button type="button" className="btn btn-light btn-sm" aria-label="Next page"
                  disabled={options.page >= totalPages} onClick={() => changeOptions({ page: options.page + 1 })}>
                  <i className="bx bx-chevron-right" aria-hidden="true" />
                </button>
              </nav>
              <label className="club-page-size mb-0">Rows
                <select className="custom-select custom-select-sm" value={options.limit}
                  onChange={event => changeOptions({ limit: Number(event.target.value), page: 1 })}>
                  {[25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
            </div>}
          </CardBody>
        </Card>
      </Container>
    </div></PublicRatingScope>
  )
}

const mapStateToProps = ({ organizers = {} }) => ({
  organizers: organizers.organizers,
  organizersLocations: organizers.organizersLocations,
  listLoading: organizers.listLoading,
  listError: organizers.listError,
  locationsLoading: organizers.locationsLoading,
  locationsError: organizers.locationsError,
})

export default connect(mapStateToProps, { getOrganizers, getOrganizersLocations })(withRouter(OrganizerDirectory))
